<template>
  <div>
    <PhotoCarousel />
    
        <!-- About section -->
        <section class="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="text-center mb-16 animate-fade-in">
              <h2 class="section-title">Notre Harmonie</h2>
              <p class="section-subtitle">
                Depuis 1874, la Royale Harmonie Émulation de Stavelot perpétue la tradition musicale 
                et contribue à la vie culturelle de notre région. En 2024, nous avons célébré notre 150ème anniversaire !
              </p>
            </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="text-center group hover-lift">
            <div class="bg-gradient-to-br from-primary-100 to-primary-200 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300">
              <svg class="w-10 h-10 text-primary-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-navy-800 mb-3 group-hover:text-primary-600 transition-colors duration-300">Tradition</h3>
            <p class="text-navy-600 leading-relaxed">
              Plus de 150 ans d'histoire musicale et de passion partagée depuis 1874.
            </p>
          </div>
          
          <div class="text-center group hover-lift">
            <div class="bg-gradient-to-br from-gold-100 to-gold-200 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300">
              <svg class="w-10 h-10 text-gold-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-navy-800 mb-3 group-hover:text-gold-600 transition-colors duration-300">70 musiciens</h3>
            <p class="text-navy-600 leading-relaxed">
              Une harmonie d'une septantaine de musiciens de tout âge et de tous horizons.
            </p>
          </div>
          
          <div class="text-center group hover-lift">
            <div class="bg-gradient-to-br from-accent-100 to-accent-200 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300">
              <svg class="w-10 h-10 text-accent-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-navy-800 mb-3 group-hover:text-accent-600 transition-colors duration-300">Dynamique</h3>
            <p class="text-navy-600 leading-relaxed">
              Entre ses concerts, les carnavals, le Laetare, et diverses prestations en Belgique et à l'étranger.
            </p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Upcoming performances -->
    <section class="py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="section-title">Prochaines Prestations</h2>
          <p class="section-subtitle">
            Découvrez nos prochains concerts et événements musicaux
          </p>
        </div>
        
        <div v-if="loadingPerformances" class="flex justify-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
        
        <div v-else-if="upcomingPerformances.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="performance in upcomingPerformances.slice(0, 3)" 
            :key="performance.id"
            class="card hover:shadow-lg transition-shadow duration-300"
          >
            <div class="flex items-center mb-4">
              <div class="bg-primary-100 text-primary-600 px-3 py-1 rounded-full text-sm font-medium">
                {{ formatDate(performance.date) }}
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-2">{{ performance.title }}</h3>
            
            <p v-if="performance.description" class="text-gray-600 mb-4 line-clamp-2">
              {{ performance.description }}
            </p>
            
            <div class="space-y-2 text-sm text-gray-500">
              <div v-if="performance.location" class="flex items-center">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                {{ performance.location }}
              </div>
              <div v-if="performance.price" class="flex items-center">
                <div class="w-6 h-6 bg-gold-100 rounded-lg flex items-center justify-center mr-2">
                  <span class="text-gold-600 font-bold text-sm">€</span>
                </div>
                {{ performance.price }}
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="text-center text-gray-500">
          <p>Aucune prestation prévue pour le moment.</p>
        </div>
        
        <div class="text-center mt-8">
          <router-link to="/performances" class="btn-primary">
            Voir toutes les prestations
          </router-link>
        </div>
      </div>
    </section>
    
    <!-- Videos section -->
    <VideoSection />
    
    <!-- Sponsors section -->
    <SponsorsSection />
    
    <!-- News section -->
    <NewsSection />
  </div>
</template>

<script>
import { performanceService } from '../services/performanceService'
import PhotoCarousel from '../components/PhotoCarousel.vue'
import VideoSection from '../components/VideoSection.vue'
import SponsorsSection from '../components/SponsorsSection.vue'
import NewsSection from '../components/NewsSection.vue'

export default {
  name: 'Home',
  components: {
    PhotoCarousel,
    VideoSection,
    SponsorsSection,
    NewsSection
  },
  data() {
    return {
      upcomingPerformances: [],
      loadingPerformances: true
    }
  },
  async mounted() {
    await this.loadUpcomingPerformances()
  },
  methods: {
    async loadUpcomingPerformances() {
      try {
        this.upcomingPerformances = await performanceService.getUpcomingPerformances()
      } catch (error) {
        console.error('Error loading upcoming performances:', error)
      } finally {
        this.loadingPerformances = false
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
